// The sign-in editor's trusted tokens over the settings document (ADR-092): the third-party JWT
// issuers the proxy accepts, at most 20, each edited as form values with lists one per line.
// Pure and DOM-free. Nothing here is secret: the proxy checks a token against the issuer's public
// keys. Ids are the server's and are never shown; a new row goes out with none.
import type { components } from "../../api/schema";
import type { FieldErrors } from "../../primitives/forms";
import { getPath, isObject, type Json, type JsonObject } from "../../settings/inherit";
import { HTTPS_URL, lines, nullIfBlank, strings, text } from "./json-values";

export type TrustedTokenMode = components["schemas"]["TrustedTokenMode"];
export type TrustedTokenAlgorithm = components["schemas"]["TrustedTokenAlgorithm"];

export const TRUSTED_TOKENS_MAX = 20;
export const TOKEN_LIFE_MIN = 60;
export const TOKEN_LIFE_MAX = 86400;
export const TOKEN_LIFE_DEFAULT = 3600;
const LABEL_MAX = 64;
const AUDIENCES_MAX = 20;
const AUDIENCE_MAX = 256;
const SUBJECTS_MAX = 500;
const SUBJECT_MAX = 183;

/** Asymmetric only: `none` and the HMAC family can never be allowed. */
export const TOKEN_ALGORITHMS: readonly TrustedTokenAlgorithm[] = ["RS256", "RS384", "RS512", "ES256", "ES384", "ES512", "PS256", "PS384", "PS512", "EdDSA"];

export const TOKEN_MODES: readonly { value: TrustedTokenMode; label: string }[] = [
  { value: "exchange", label: "Exchange for a machine key" },
  { value: "direct", label: "Bearer on model requests" },
  { value: "both", label: "Either" },
];

// The settings schema's claim name.
const CLAIM = /^[A-Za-z0-9_.:/-]{1,128}$/;

/** One trusted token row as the form edits it: lists one per line, numbers as text. */
export interface TrustedTokenValues {
  /** The server's id, carried back on a write and never shown; "" for a row not saved yet. */
  id: string;
  label: string;
  issuer: string;
  jwksUri: string;
  audiences: string;
  algorithms: TrustedTokenAlgorithm[];
  subjectClaim: string;
  emailClaim: string;
  groupsClaim: string;
  allowedSubjects: string;
  maxTokenLifeSeconds: string;
  mode: TrustedTokenMode;
  internal: boolean;
}

export function blankTrustedToken(): TrustedTokenValues {
  return {
    id: "",
    label: "",
    issuer: "",
    jwksUri: "",
    audiences: "",
    algorithms: ["RS256"],
    subjectClaim: "sub",
    emailClaim: "email",
    groupsClaim: "",
    allowedSubjects: "",
    maxTokenLifeSeconds: String(TOKEN_LIFE_DEFAULT),
    mode: "exchange",
    internal: false,
  };
}

/** The issuer as a token's `iss` is compared with it: scheme and host lower-cased, no trailing slash. */
export function canonicalIssuer(raw: string): string {
  const trimmed = raw.trim();
  const match = /^([A-Za-z][A-Za-z0-9+.-]*:\/\/)([^/?#]*)(.*)$/.exec(trimmed);
  if (!match) return trimmed;
  const [, scheme = "", host = "", rest = ""] = match;
  return (scheme.toLowerCase() + host.toLowerCase() + rest).replace(/\/$/, "");
}

/** Toggle one algorithm, keeping the canonical order. */
export function toggleAlgorithm(current: readonly TrustedTokenAlgorithm[], algorithm: TrustedTokenAlgorithm): TrustedTokenAlgorithm[] {
  const next = new Set(current);
  if (next.has(algorithm)) next.delete(algorithm);
  else next.add(algorithm);
  return TOKEN_ALGORITHMS.filter((a) => next.has(a));
}

export function tokensOf(document: Json | undefined): JsonObject[] {
  const list = getPath(document, "sign_in.trusted_tokens");
  return Array.isArray(list) ? list.filter(isObject) : [];
}

export function tokenFromDoc(doc: JsonObject): TrustedTokenValues {
  const blank = blankTrustedToken();
  const algorithms = Array.isArray(doc["algorithms"]) ? (doc["algorithms"].filter((a): a is string => typeof a === "string") as TrustedTokenAlgorithm[]) : blank.algorithms;
  return {
    id: text(doc["id"]),
    label: text(doc["label"]),
    issuer: text(doc["issuer"]),
    jwksUri: text(doc["jwks_uri"]),
    audiences: strings(doc["audiences"]),
    algorithms,
    subjectClaim: text(doc["subject_claim"]),
    emailClaim: text(doc["email_claim"]),
    groupsClaim: text(doc["groups_claim"]),
    allowedSubjects: strings(doc["allowed_subjects"]),
    maxTokenLifeSeconds: String(typeof doc["max_token_life_seconds"] === "number" ? doc["max_token_life_seconds"] : blank.maxTokenLifeSeconds),
    mode: (text(doc["mode"]) || blank.mode) as TrustedTokenMode,
    internal: doc["internal"] === true,
  };
}

export function tokenToDoc(values: TrustedTokenValues): JsonObject {
  const doc: JsonObject = {};
  if (values.id !== "") doc["id"] = values.id;
  doc["label"] = values.label.trim();
  doc["issuer"] = canonicalIssuer(values.issuer);
  doc["jwks_uri"] = values.jwksUri.trim();
  doc["audiences"] = lines(values.audiences);
  doc["algorithms"] = [...values.algorithms];
  doc["subject_claim"] = values.subjectClaim.trim();
  doc["email_claim"] = nullIfBlank(values.emailClaim);
  doc["groups_claim"] = nullIfBlank(values.groupsClaim);
  doc["allowed_subjects"] = lines(values.allowedSubjects);
  doc["max_token_life_seconds"] = Number(values.maxTokenLifeSeconds.trim());
  doc["mode"] = values.mode;
  doc["internal"] = values.internal;
  return doc;
}

function wholeNumber(raw: string, min: number, max: number): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return value < min || value > max ? null : value;
}

function listProblem(values: readonly string[], min: number, max: number, longest: number, noun: string): string | null {
  if (values.length < min) return min === 1 ? `At least one ${noun}.` : `At least ${min} ${noun}s.`;
  if (values.length > max) return `At most ${max} ${noun}s.`;
  const seen = new Set<string>();
  for (const value of values) {
    if (value.length > longest) return `${noun} longer than ${longest} characters.`;
    if (seen.has(value)) return `${value} is listed twice.`;
    seen.add(value);
  }
  return null;
}

/** The form error key of one row's field. */
export function tokenErrorKey(index: number, field: string): string {
  return `trustedTokens.${index}.${field}`;
}

/** Errors by `trustedTokens.<i>.<field>` (and `trustedTokens` for the list), the schema's rules. */
export function validateTrustedTokens(tokens: readonly TrustedTokenValues[]): FieldErrors {
  const errors: FieldErrors = {};
  if (tokens.length > TRUSTED_TOKENS_MAX) errors["trustedTokens"] = `At most ${TRUSTED_TOKENS_MAX} trusted issuers.`;
  const issuers = new Set<string>();
  tokens.forEach((t, i) => {
    const at = (field: string) => tokenErrorKey(i, field);
    if (t.label.trim().length > LABEL_MAX) errors[at("label")] = `At most ${LABEL_MAX} characters.`;
    const issuer = canonicalIssuer(t.issuer);
    if (!HTTPS_URL.test(issuer)) errors[at("issuer")] = "An https URL with no query or fragment.";
    else if (issuers.has(issuer)) errors[at("issuer")] = "This issuer is listed twice.";
    issuers.add(issuer);
    if (!HTTPS_URL.test(t.jwksUri.trim())) errors[at("jwksUri")] = "An https URL with no query or fragment.";
    const audiences = listProblem(lines(t.audiences), 1, AUDIENCES_MAX, AUDIENCE_MAX, "audience");
    if (audiences !== null) errors[at("audiences")] = audiences;
    if (t.algorithms.length === 0) errors[at("algorithms")] = "Pick at least one algorithm.";
    if (t.subjectClaim.trim() !== "" && !CLAIM.test(t.subjectClaim.trim())) errors[at("subjectClaim")] = "A claim name.";
    if (t.emailClaim.trim() !== "" && !CLAIM.test(t.emailClaim.trim())) errors[at("emailClaim")] = "A claim name, or empty for none.";
    if (t.groupsClaim.trim() !== "" && !CLAIM.test(t.groupsClaim.trim())) errors[at("groupsClaim")] = "A claim name, or empty for none.";
    const subjects = listProblem(lines(t.allowedSubjects), 0, SUBJECTS_MAX, SUBJECT_MAX, "subject");
    if (subjects !== null) errors[at("allowedSubjects")] = subjects;
    if (wholeNumber(t.maxTokenLifeSeconds, TOKEN_LIFE_MIN, TOKEN_LIFE_MAX) === null) {
      errors[at("maxTokenLifeSeconds")] = `Seconds, between ${TOKEN_LIFE_MIN} and ${TOKEN_LIFE_MAX}.`;
    }
  });
  return errors;
}
